import { useCallback, useEffect, useMemo, useState } from "react";

const DEBOUNCE_MS = 300;

function useDebouncedValue(value, ms) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

function getSearchParams() {
  const p = new URLSearchParams(window.location.search);
  return {
    query: p.get("query") ?? "",
    page: Math.max(1, parseInt(p.get("page") || "1", 10) || 1),
    per_page: Math.max(1, Math.min(100, parseInt(p.get("per_page") || "10", 10) || 10)),
    sort: p.get("sort") ?? "",
    order: p.get("order") ?? "asc",
  };
}

function replaceSearchParams({ query, page, per_page, sort, order }) {
  const p = new URLSearchParams();
  if (query) p.set("query", query);
  if (page > 1) p.set("page", String(page));
  if (per_page !== 10) p.set("per_page", String(per_page));
  if (sort) p.set("sort", sort);
  if (order && order !== "asc") p.set("order", order);
  const qs = p.toString();
  const url = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
  window.history.replaceState(null, "", url);
}

export default function useListPageUrlState(options = {}) {
  const { defaultPerPage = 10 } = options;
  const [params, setParams] = useState(() => {
    const s = getSearchParams();
    return { ...s, per_page: s.per_page || defaultPerPage };
  });

  useEffect(() => {
    const onPopState = () => setParams(getSearchParams());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const updateUrl = useCallback((next) => {
    setParams((prev) => {
      const merged = { ...prev, ...next };
      replaceSearchParams(merged);
      return merged;
    });
  }, []);

  const setQuery = useCallback(
    (q) => updateUrl({ query: typeof q === "function" ? q(params.query) : q, page: 1 }),
    [updateUrl, params.query]
  );
  const setPage = useCallback((p) => updateUrl({ page: typeof p === "function" ? p(params.page) : p }), [updateUrl, params.page]);
  const setPerPage = useCallback(
    (pp) => updateUrl({ per_page: typeof pp === "function" ? pp(params.per_page) : pp, page: 1 }),
    [updateUrl, params.per_page]
  );

  const setSort = useCallback(
    (sort, order) => updateUrl({ sort: sort ?? "", order: order ?? "asc", page: 1 }),
    [updateUrl]
  );

  const debouncedQuery = useDebouncedValue(params.query, DEBOUNCE_MS);

  return useMemo(
    () => ({
      query: params.query,
      debouncedQuery,
      setQuery,
      page: params.page,
      setPage,
      perPage: params.per_page,
      setPerPage,
      sort: params.sort,
      order: params.order,
      setSort,
      fetchParams: {
        query: debouncedQuery,
        page: params.page,
        per_page: params.per_page,
        sort: params.sort || undefined,
        order: params.order || undefined,
      },
    }),
    [params.query, params.page, params.per_page, params.sort, params.order, debouncedQuery, setQuery, setPage, setPerPage, setSort]
  );
}
